import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSession } from '@clerk/clerk-react';
import { Input, Button, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Spinner } from '@heroui/react';
import { MagnifyingGlass, Buildings, X } from '@phosphor-icons/react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { toast } from 'react-toastify';
import { ResourceType } from 'shared/types/resource';
import { ResourceValue } from './ResourceSearchSelect';

interface CreateContactModalProps {
    isOpen: boolean;
    onClose: () => void;
    onContactCreated: (contact: ResourceValue) => void;
    initialName?: string;
}

interface CreateContactForm {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    functionTitle: string;
}

interface CompanySearchResult {
    id: number;
    name: string;
    vatNumber?: string;
    email?: string;
    phone?: string;
}

// Phone format: +32/+31/+33 followed by 9 digits, first digit cannot be 0
// Examples: +32479442152, +31612345678
const PHONE_REGEX = /^\+3[123][1-9]\d{8}$/;

const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters except +
    let cleaned = value.replace(/[^\d+]/g, '');

    // Ensure it starts with +
    if (cleaned && !cleaned.startsWith('+')) {
        // If starts with 00, replace with +
        if (cleaned.startsWith('00')) {
            cleaned = '+' + cleaned.slice(2);
        } else if (cleaned.startsWith('0')) {
            // Belgian number starting with 0, assume +32
            cleaned = '+32' + cleaned.slice(1);
        }
    }

    // Remove leading 0 after country code (+32, +31, +33)
    if (cleaned.match(/^\+3[123]0/)) {
        cleaned = cleaned.slice(0, 3) + cleaned.slice(4);
    }

    return cleaned;
};

const validatePhone = (phone: string): { valid: boolean; error?: string } => {
    if (!phone) return { valid: true }; // Empty is ok (optional field)

    if (!phone.startsWith('+32') && !phone.startsWith('+31') && !phone.startsWith('+33')) {
        return { valid: false, error: 'Moet beginnen met +32, +31 of +33' };
    }

    // Check for 0 after country code
    if (phone.match(/^\+3[123]0/)) {
        return { valid: false, error: 'Geen 0 na landcode' };
    }

    if (!PHONE_REGEX.test(phone)) {
        return { valid: false, error: 'Formaat: +32xxxxxxxxx (9 cijfers)' };
    }

    return { valid: true };
};

export const CreateContactModal: React.FC<CreateContactModalProps> = ({
    isOpen,
    onClose,
    onContactCreated,
    initialName = ''
}) => {
    const { session } = useSession();
    const [isCreating, setIsCreating] = useState(false);
    const [createForm, setCreateForm] = useState<CreateContactForm>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        functionTitle: ''
    });
    const [phoneError, setPhoneError] = useState<string | undefined>();

    // Company state
    const [selectedCompany, setSelectedCompany] = useState<CompanySearchResult | null>(null);
    const [companySearchTerm, setCompanySearchTerm] = useState('');
    const [companySearchResults, setCompanySearchResults] = useState<CompanySearchResult[]>([]);
    const [isSearchingCompany, setIsSearchingCompany] = useState(false);
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [newCompanyName, setNewCompanyName] = useState(''); // For creating new company with contact

    const companySearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const companyContainerRef = useRef<HTMLDivElement>(null);

    // Initialize form with name when modal opens
    useEffect(() => {
        if (isOpen && initialName) {
            const nameParts = initialName.trim().split(' ');
            setCreateForm(prev => ({
                ...prev,
                firstName: nameParts[0] || '',
                lastName: nameParts.slice(1).join(' ') || ''
            }));
        }
    }, [isOpen, initialName]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setCreateForm({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                functionTitle: ''
            });
            setPhoneError(undefined);
            setSelectedCompany(null);
            setCompanySearchTerm('');
            setCompanySearchResults([]);
            setShowCompanyDropdown(false);
            setNewCompanyName('');
        }
    }, [isOpen]);

    // Close company dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (companyContainerRef.current && !companyContainerRef.current.contains(event.target as Node)) {
                setShowCompanyDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Search companies
    const searchCompanies = useCallback(async (query: string) => {
        if (!query || query.length < 2) {
            setCompanySearchResults([]);
            return;
        }

        setIsSearchingCompany(true);
        try {
            const response = await fetchWithAuth(
                `${API_URL}/contact/search-companies?query=${encodeURIComponent(query)}`,
                { method: 'GET' },
                session
            );

            if (response.ok) {
                const companies = await response.json();
                setCompanySearchResults(companies.map((c: any) => ({
                    id: c.id,
                    name: c.name,
                    vatNumber: c.vatNumber,
                    email: c.email,
                    phone: c.phone_direct
                })));
            }
        } catch (error) {
            console.error('Error searching companies:', error);
        } finally {
            setIsSearchingCompany(false);
        }
    }, [session]);

    // Debounced company search
    useEffect(() => {
        if (companySearchTimeoutRef.current) {
            clearTimeout(companySearchTimeoutRef.current);
        }

        if (companySearchTerm.length >= 2) {
            companySearchTimeoutRef.current = setTimeout(() => {
                searchCompanies(companySearchTerm);
            }, 300);
        } else {
            setCompanySearchResults([]);
        }

        return () => {
            if (companySearchTimeoutRef.current) {
                clearTimeout(companySearchTimeoutRef.current);
            }
        };
    }, [companySearchTerm, searchCompanies]);

    // Handle phone change with formatting
    const handlePhoneChange = useCallback((value: string) => {
        const formatted = formatPhoneNumber(value);
        setCreateForm(prev => ({ ...prev, phone: formatted }));

        // Validate and set error
        const validation = validatePhone(formatted);
        setPhoneError(validation.error);
    }, []);

    // Handle selecting to create a new company
    const handleSelectNewCompany = useCallback((name: string) => {
        setNewCompanyName(name);
        setSelectedCompany(null);
        setShowCompanyDropdown(false);
        setCompanySearchTerm('');
    }, []);

    // Create contact (and company if new)
    const handleCreateContact = useCallback(async () => {
        if (!createForm.firstName.trim() || !createForm.lastName.trim()) {
            toast.error('Voornaam en achternaam zijn verplicht');
            return;
        }

        // Validate phone
        const phoneValidation = validatePhone(createForm.phone);
        if (!phoneValidation.valid) {
            setPhoneError(phoneValidation.error);
            toast.error(phoneValidation.error);
            return;
        }

        setIsCreating(true);
        try {
            let companyId = selectedCompany?.id;
            let companyName = selectedCompany?.name;

            // If creating a new company, do it first (fast)
            if (newCompanyName && !selectedCompany) {
                const companyResponse = await fetchWithAuth(
                    `${API_URL}/contact/create-company`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ name: newCompanyName.trim() })
                    },
                    session
                );

                if (companyResponse.ok) {
                    const newCompany = await companyResponse.json();
                    companyId = newCompany.id;
                    companyName = newCompany.name;
                } else {
                    console.error('Failed to create company, continuing with contact only');
                }
            }

            // Create contact and resource in parallel for speed
            const contactPromise = fetchWithAuth(
                `${API_URL}/contact/create-contact`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        firstName: createForm.firstName.trim(),
                        lastName: createForm.lastName.trim(),
                        email: createForm.email.trim() || undefined,
                        phone_mobile: createForm.phone.trim() || undefined,
                        jobTitle: createForm.functionTitle.trim() || undefined,
                        company: companyId ? { id: companyId } : undefined,
                        tags: []
                    })
                },
                session
            );

            const contactResponse = await contactPromise;

            if (!contactResponse.ok) {
                const errorData = await contactResponse.json();
                toast.error(errorData.message || 'Fout bij aanmaken contact');
                return;
            }

            const newContact = await contactResponse.json();
            const contactName = `${createForm.firstName.trim()} ${createForm.lastName.trim()}`;

            // Create resource entry (for acquisition linking)
            const resourceResponse = await fetchWithAuth(
                `${API_URL}/resource`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        type: 'contact',
                        resourceId: newContact.id.toString(),
                        name: contactName,
                        metadata1: createForm.phone.trim() || null,
                        metadata2: createForm.email.trim() || null
                    })
                },
                session
            );

            let resourceId = newContact.id;
            if (resourceResponse.ok) {
                const resource = await resourceResponse.json();
                resourceId = resource.id;
            }

            toast.success(`${contactName} aangemaakt${newCompanyName ? ` bij ${companyName}` : ''}`);

            onContactCreated({
                id: resourceId,
                type: ResourceType.CONTACT,
                resourceId: newContact.id.toString(),
                name: contactName,
                phone: createForm.phone.trim() || undefined,
                email: createForm.email.trim() || undefined,
                companyName: companyName,
                companyId: companyId,
                functionTitle: createForm.functionTitle.trim() || undefined
            });

            onClose();
        } catch (error) {
            console.error('Error creating contact:', error);
            toast.error('Fout bij aanmaken contact');
        } finally {
            setIsCreating(false);
        }
    }, [createForm, selectedCompany, newCompanyName, session, onContactCreated, onClose]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            isDismissable={false}
            hideCloseButton={false}
        >
            <ModalContent>
                <ModalHeader>Nieuw contact aanmaken</ModalHeader>
                <ModalBody className="gap-4">
                    {/* Name fields */}
                    <div className="grid grid-cols-2 gap-4">
                        <Input
                            label="Voornaam *"
                            placeholder="Jan"
                            value={createForm.firstName}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, firstName: e.target.value }))}
                            classNames={{ inputWrapper: "bg-white" }}
                            autoFocus
                        />
                        <Input
                            label="Achternaam *"
                            placeholder="Janssens"
                            value={createForm.lastName}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, lastName: e.target.value }))}
                            classNames={{ inputWrapper: "bg-white" }}
                        />
                    </div>

                    {/* Company search/select */}
                    <div ref={companyContainerRef} className="relative">
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                            Bedrijf
                        </label>
                        {selectedCompany ? (
                            <div className="flex items-center gap-2 p-2.5 bg-white border border-neutral-primary/20 rounded-lg">
                                <Buildings className="h-5 w-5 text-moss-primary" />
                                <div className="flex-1">
                                    <span className="text-sm font-medium">{selectedCompany.name}</span>
                                    {selectedCompany.vatNumber && (
                                        <span className="text-xs text-neutral-400 ml-2">
                                            ({selectedCompany.vatNumber})
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setSelectedCompany(null)}
                                    className="p-1 hover:bg-neutral-100 rounded"
                                >
                                    <X className="h-4 w-4 text-neutral-400" />
                                </button>
                            </div>
                        ) : newCompanyName ? (
                            <div className="flex items-center gap-2 p-2.5 bg-moss-primary/10 border border-moss-primary/30 rounded-lg">
                                <Buildings className="h-5 w-5 text-moss-primary" />
                                <div className="flex-1">
                                    <span className="text-sm font-medium text-moss-primary">
                                        {newCompanyName}
                                    </span>
                                    <span className="text-xs text-moss-primary/70 ml-2">(nieuw)</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setNewCompanyName('')}
                                    className="p-1 hover:bg-moss-primary/20 rounded"
                                >
                                    <X className="h-4 w-4 text-moss-primary" />
                                </button>
                            </div>
                        ) : (
                            <>
                                <Input
                                    placeholder="Zoek of typ nieuwe bedrijfsnaam..."
                                    value={companySearchTerm}
                                    onChange={(e) => setCompanySearchTerm(e.target.value)}
                                    onFocus={() => setShowCompanyDropdown(true)}
                                    startContent={<MagnifyingGlass className="h-4 w-4 text-neutral-400" />}
                                    endContent={isSearchingCompany && <Spinner size="sm" />}
                                    classNames={{
                                        base: "w-full",
                                        inputWrapper: "bg-white h-10"
                                    }}
                                />
                                {showCompanyDropdown && (
                                    <div className="absolute left-0 right-0 top-full mt-1 z-50 max-h-48 overflow-auto rounded-lg border border-neutral-primary/20 bg-white shadow-lg">
                                        {companySearchTerm.length >= 2 && companySearchResults.length > 0 && (
                                            <>
                                                <div className="px-3 py-1.5 text-xs font-semibold text-neutral-500 bg-neutral-50 border-b">
                                                    Bestaande bedrijven
                                                </div>
                                                {companySearchResults.map((company) => (
                                                    <div
                                                        key={company.id}
                                                        onClick={() => {
                                                            setSelectedCompany(company);
                                                            setShowCompanyDropdown(false);
                                                            setCompanySearchTerm('');
                                                        }}
                                                        className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-moss-primary/10 border-b border-neutral-100"
                                                    >
                                                        <Buildings className="h-4 w-4 text-neutral-400" />
                                                        <div className="flex-1">
                                                            <span className="text-sm font-medium">{company.name}</span>
                                                            {company.vatNumber && (
                                                                <span className="text-xs text-neutral-400 ml-2">
                                                                    {company.vatNumber}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                        {companySearchTerm.length >= 2 && companySearchResults.length === 0 && !isSearchingCompany && (
                                            <div className="px-3 py-2 text-sm text-neutral-500">
                                                Geen bestaand bedrijf gevonden
                                            </div>
                                        )}
                                        {companySearchTerm.length < 2 && (
                                            <div className="px-3 py-3 text-center text-sm text-neutral-500">
                                                Typ minstens 2 tekens
                                            </div>
                                        )}
                                        {/* Create new company option */}
                                        {companySearchTerm.length >= 2 && (
                                            <div
                                                onClick={() => handleSelectNewCompany(companySearchTerm)}
                                                className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-moss-primary/10 bg-moss-primary/5 border-t"
                                            >
                                                <Buildings className="h-4 w-4 text-moss-primary" />
                                                <span className="text-sm font-medium text-moss-primary">
                                                    "{companySearchTerm}" als nieuw bedrijf
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Function title */}
                    <Input
                        label="Functie"
                        placeholder="Makelaar, Eigenaar, ..."
                        value={createForm.functionTitle}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, functionTitle: e.target.value }))}
                        classNames={{ inputWrapper: "bg-white" }}
                    />

                    {/* Email */}
                    <Input
                        label="E-mail"
                        type="email"
                        placeholder="jan@voorbeeld.be"
                        value={createForm.email}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                        classNames={{ inputWrapper: "bg-white" }}
                    />

                    {/* Phone with format validation */}
                    <Input
                        label="Telefoon"
                        placeholder="+32479442152"
                        value={createForm.phone}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        classNames={{ inputWrapper: "bg-white" }}
                        isInvalid={!!phoneError}
                        errorMessage={phoneError}
                        description="Formaat: +32xxxxxxxxx (9 cijfers, geen 0 na landcode)"
                    />
                </ModalBody>
                <ModalFooter>
                    <Button
                        variant="light"
                        onPress={onClose}
                    >
                        Annuleren
                    </Button>
                    <Button
                        color="primary"
                        onPress={handleCreateContact}
                        isLoading={isCreating}
                        className="bg-moss-primary"
                    >
                        {newCompanyName ? 'Contact + Bedrijf aanmaken' : 'Aanmaken'}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CreateContactModal;
